// @ts-check
// Peças comuns das telas do painel de suporte (contrato 08 de cobrança e operação, seção 7.4): cabeçalho e corpo da
// página no mesmo card do CRM, números em destaque, cartões de seção, listas de definição, etiquetas de situação do
// Asaas e os formatos de dinheiro, documento e telefone que o Asaas devolve.
import { html } from 'htm/preact';
import { Link } from '../../../app/router.js';
import { Icon } from '../../../ui/icon.js';
import { Tag } from '../../../ui/chip.js';
import { Skeleton } from '../../../ui/feedback.js';
import { formatDate, formatDateTime } from '../../../lib/format.js';
import { adminInstitutionPath, invoiceStatusColor, paymentStatusColor, siteOrderStatusColor } from '../../../lib/admin-navigation.js';
import { subscriptionStatusColor } from '../operations-format.js';
import { hasTranslation, t } from '../../../i18n/index.js';

const CURRENCY_FORMATTER = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const INTEGER_FORMATTER = new Intl.NumberFormat('pt-BR');

/** Valor em reais como o Asaas devolve (número decimal). @param {number | null | undefined} value */
export function formatMoney(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
  return CURRENCY_FORMATTER.format(Number(value));
}

/** @param {number | null | undefined} value */
export function formatInteger(value) {
  return INTEGER_FORMATTER.format(Number(value ?? 0));
}

/** CPF ou CNPJ só com dígitos, com a pontuação. @param {string | null | undefined} value */
export function formatTaxDocument(value) {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return value ? String(value) : '-';
}

/** Data AAAA-MM-DD do Asaas ou ISO completa. @param {string | null | undefined} value */
export function formatDay(value) {
  if (!value) return '-';
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? formatDate(`${value}T12:00:00.000Z`) : formatDateTime(value);
}

/** Texto do enum pelo catálogo do painel, com o código como reserva. @param {string} group @param {string | null | undefined} value */
export function enumLabel(group, value) {
  if (!value) return '-';
  const key = `operations.admin.panel.${group}.${value}`;
  return hasTranslation(key) ? t(key) : value;
}

/** Forma de pagamento do Asaas, com o texto já usado em Configurações > Assinatura. @param {string | null | undefined} value */
export function billingTypeLabel(value) {
  if (!value) return '-';
  const key = `operations.billing.billingTypes.${value}`;
  return hasTranslation(key) ? t(key) : value;
}

/**
 * @param {{ icon: string, title: string, subtitle?: string | null, actions?: any, back?: { href: string, label: string } | null, children: any, wide?: boolean }} props
 */
export function AdminPage({ icon, title, subtitle, actions, back, children, wide = false }) {
  return html`<div class="page-card-wrapper">
    <section class="page-card admin-card">
      <header class="page-header">
        <div class="page-header-left">
          ${back ? html`<${Link} href=${back.href} className="admin-back-link" aria-label=${back.label}><${Icon} name="IconArrowLeft" size=${16} /></${Link}>` : null}
          <div class="page-header-title"><${Icon} name=${icon} size=${16} /><span class="page-header-title-text">${title}</span></div>
        </div>
        <div class="page-header-right">${actions ?? null}</div>
      </header>
      <div class="page-card-body admin-body" data-wide=${wide ? 'true' : undefined}>
        <div class="admin-content">
          ${subtitle ? html`<p class="admin-subtitle">${subtitle}</p>` : null}
          ${children}
        </div>
      </div>
    </section>
  </div>`;
}

/**
 * Cartão de seção com título, texto de apoio e ações à direita.
 * @param {{ title: string, description?: string | null, actions?: any, children: any, className?: string, testId?: string }} props
 */
export function AdminSection({ title, description, actions, children, className = '', testId }) {
  return html`<section class=${`admin-section ${className}`} data-testid=${testId}>
    <div class="admin-section-head">
      <div class="admin-section-titles">
        <h2 class="admin-section-title">${title}</h2>
        ${description ? html`<p class="admin-section-description">${description}</p>` : null}
      </div>
      ${actions ? html`<div class="admin-section-actions">${actions}</div>` : null}
    </div>
    ${children}
  </section>`;
}

/**
 * Número em destaque; `tone` colore o valor (verde recebido, laranja em aberto, vermelho atrasado).
 * @param {{ label: string, value: any, hint?: string | null, tone?: 'default' | 'positive' | 'warning' | 'danger' | 'muted', icon?: string, href?: string | null }} props
 */
export function StatTile({ label, value, hint, tone = 'default', icon, href }) {
  const body = html`<span class="admin-stat-label">${icon ? html`<${Icon} name=${icon} size=${14} />` : null}${label}</span>
    <span class="admin-stat-value" data-tone=${tone}>${value}</span>
    ${hint ? html`<span class="admin-stat-hint">${hint}</span>` : null}`;
  if (href) return html`<${Link} href=${href} className="admin-stat admin-stat-link">${body}</${Link}>`;
  return html`<div class="admin-stat">${body}</div>`;
}

/** @param {{ children: any, columns?: number }} props */
export function StatGrid({ children, columns = 4 }) {
  return html`<div class="admin-stat-grid" style=${`--admin-stat-columns: ${columns}`}>${children}</div>`;
}

/**
 * Lista rótulo e valor em duas colunas, como a ficha de um registro.
 * @param {{ items: Array<{ label: string, value: any, mono?: boolean } | null | false>, columns?: number }} props
 */
export function DefinitionList({ items, columns = 2 }) {
  return html`<dl class="admin-definitions" style=${`--admin-definition-columns: ${columns}`}>
    ${items.filter(Boolean).map((item) => html`<div key=${/** @type {any} */ (item).label} class="admin-definition">
      <dt>${/** @type {any} */ (item).label}</dt>
      <dd class=${/** @type {any} */ (item).mono ? 'settings-table-mono' : ''}>${/** @type {any} */ (item).value ?? '-'}</dd>
    </div>`)}
  </dl>`;
}

/** @param {{ icon?: string, title: string, subtitle?: string | null, action?: any }} props */
export function AdminEmpty({ icon = 'IconInbox', title, subtitle, action }) {
  return html`<div class="admin-empty">
    <${Icon} name=${icon} size=${20} />
    <span class="admin-empty-title">${title}</span>
    ${subtitle ? html`<span class="admin-empty-subtitle">${subtitle}</span>` : null}
    ${action ?? null}
  </div>`;
}

/** @param {{ rows?: number }} props */
export function AdminLoading({ rows = 3 }) {
  return html`<div class="admin-loading">${Array.from({ length: rows }, (_, index) => html`<${Skeleton} key=${index} height=${56} radius=${8} />`)}</div>`;
}

/** Aviso de que o Asaas não está configurado ou não respondeu. @param {{ isProviderConfigured: boolean, error?: string | null }} props */
export function ProviderNotice({ isProviderConfigured, error }) {
  if (isProviderConfigured && !error) return null;
  const message = !isProviderConfigured ? t('operations.admin.panel.provider.notConfigured') : t('operations.admin.panel.provider.unavailable', { code: error ?? '' });
  return html`<div class="admin-notice" data-tone=${isProviderConfigured ? 'danger' : 'muted'} role="status">
    <${Icon} name=${isProviderConfigured ? 'IconAlertTriangle' : 'IconInfoCircle'} size=${16} /><span>${message}</span>
  </div>`;
}

/** @param {{ status: string | null | undefined }} props */
export function PaymentStatusTag({ status }) {
  return html`<${Tag} color=${paymentStatusColor(status)} text=${enumLabel('paymentStatuses', status)} />`;
}

/** @param {{ status: string | null | undefined }} props */
export function InvoiceStatusTag({ status }) {
  return html`<${Tag} color=${invoiceStatusColor(status)} text=${enumLabel('invoiceStatuses', status)} />`;
}

/** @param {{ status: string | null | undefined }} props */
export function SiteOrderStatusTag({ status }) {
  return html`<${Tag} color=${siteOrderStatusColor(status)} text=${enumLabel('siteOrderStatuses', status)} />`;
}

/** @param {{ status: string | null | undefined }} props */
export function SubscriptionStatusTag({ status }) {
  return html`<${Tag} color=${subscriptionStatusColor(String(status))} text=${status ? t(`operations.billing.statuses.${status}`) : '-'} />`;
}

/**
 * Dono de uma cobrança ou nota: a instituição (link para a ficha) ou o pedido do site ainda sem instituição.
 * @param {{ owner: { tenantId: string | null, slug: string, displayName: string, siteOrderId: string | null } | null | undefined }} props
 */
export function OwnerLink({ owner }) {
  if (!owner) return html`<span class="settings-table-muted">${t('operations.admin.panel.unknownOwner')}</span>`;
  if (owner.tenantId) return html`<${Link} href=${adminInstitutionPath(owner.tenantId)} className="admin-owner-link"><span>${owner.displayName}</span><span class="settings-table-muted">${owner.slug}</span></${Link}>`;
  return html`<${Link} href=${`/admin/site-orders/${owner.siteOrderId ?? ''}`} className="admin-owner-link"><span>${owner.displayName}</span><span class="settings-table-muted">${t('operations.admin.panel.siteOrderOwner')}</span></${Link}>`;
}

/** @param {{ href: string | null | undefined, label: string, icon?: string }} props */
export function ExternalLink({ href, label, icon = 'IconExternalLink' }) {
  if (!href) return null;
  return html`<a class="admin-external-link" href=${href} target="_blank" rel="noopener noreferrer"><${Icon} name=${icon} size=${14} /><span>${label}</span></a>`;
}

/**
 * Campo de filtro em linha: rótulo pequeno em cima do controle.
 * @param {{ label: string, children: any }} props
 */
export function FilterField({ label, children }) {
  return html`<label class="admin-filter-field"><span class="admin-filter-label">${label}</span>${children}</label>`;
}

/** @param {{ value: string, onChange: (value: string) => void, options: Array<{ value: string, label: string }>, ariaLabel?: string }} props */
export function FilterSelect({ value, onChange, options, ariaLabel }) {
  return html`<select class="text-input admin-filter-select" value=${value} aria-label=${ariaLabel}
    onChange=${(/** @type {Event} */ event) => onChange(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
    ${options.map((option) => html`<option key=${option.value} value=${option.value} selected=${option.value === value}>${option.label}</option>`)}
  </select>`;
}

/** @param {{ value: string, onChange: (value: string) => void, ariaLabel?: string }} props */
export function FilterDate({ value, onChange, ariaLabel }) {
  return html`<input class="text-input admin-filter-date" type="date" value=${value} aria-label=${ariaLabel}
    onInput=${(/** @type {Event} */ event) => onChange(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />`;
}
